<script setup>

import {computed, onMounted, reactive, ref, watch} from "vue";
import {useAdminStore} from "@/store/admin";
import {useToast} from "vue-toast-notification";
import MainPreloader from "@/components/shared/MainPreloader.vue";
import EmptyCard from "@/components/shared/EmptyCard.vue";
import FormInput from "@/components/ui/FormInput.vue";
import ActionButton from "@/components/ui/ActionButton.vue";
import AdminModal from "@/components/AdminView/AdminModal.vue";

const adminStore = useAdminStore();
const toast = useToast();
const items = computed(() => adminStore.mailings);
const editing = ref(null);
const showForm = ref(false);
const recipientsCount = ref(null);
const testEmail = ref("");
const form = reactive(defaultForm());

const PER_PAGE = 50;

const audienceTypes = [
    {value: "all", label: "Все с email"},
    {value: "subscribed", label: "Только с флагом подписки"},
    {value: "role", label: "По роли"},
    {value: "attribute", label: "По атрибуту (info)"},
];

const sourceTypes = [
    {value: "text", label: "Свой текст"},
    {value: "news", label: "Новость"},
    {value: "product", label: "Товар"},
];

const statusLabels = {
    draft: "Черновик",
    sending: "Отправка",
    completed: "Завершено",
    completed_with_errors: "Завершено с ошибками",
};

const newsOptions = computed(() => (adminStore.news || []).map(item => ({
    value: item.id,
    label: item.title || (item.body ? String(item.body).slice(0, 60) : item.id),
})));

const productOptions = computed(() => (adminStore.products || []).map(item => ({
    value: item.id,
    label: item.nm || item.id,
})));

const roleOptions = computed(() => (adminStore.roles || []).map(role => ({
    value: role.name,
    label: role.category ? `${role.name} (${role.category})` : role.name,
})));

// Модалка отправки/повтора
const sendTarget = ref(null);
const sendMode = ref("all");
const showSend = ref(false);
const sendRecipientsCount = ref(null);

// Выбор режима имеет смысл, только если есть неудачные доставки последнего запуска
const canChooseSendMode = computed(() => (sendTarget.value?.failed_count || 0) > 0);

// Модалка протокола доставки
const protocolTarget = ref(null);
const showProtocol = ref(false);
const deliveries = ref([]);
const deliveriesTotal = ref(0);
const deliveriesRun = ref(0);
const deliveriesPage = ref(1);

// Модалка отчёта по запускам
const runsTarget = ref(null);
const showRuns = ref(false);
const runs = ref([]);

function defaultForm() {
    return {
        subject: "",
        body: "",
        signature: "",
        audience_type: "all",
        audience_value: "",
        audience_attr_value: "",
        source_type: "text",
        source_id: "",
    };
}

function resetForm() {
    Object.assign(form, defaultForm());
    testEmail.value = "";
}

function formatDate(dateStr) {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("ru-RU") + " " + d.toLocaleTimeString("ru-RU", {hour: "2-digit", minute: "2-digit"});
}

function statusLabel(status) {
    return statusLabels[status] || status || "—";
}

function audienceLabel(item) {
    const map = {all: "Все с email", subscribed: "Подписчики", role: "Роль", attribute: "Атрибут"};
    let label = map[item.audience_type] || item.audience_type || "—";
    if (item.audience_type === "role" && item.audience_value) {
        label += `: ${item.audience_value}`;
    }
    if (item.audience_type === "attribute" && item.audience_value) {
        label += `: ${item.audience_value}`;
        if (item.audience_attr_value) label += ` = ${item.audience_attr_value}`;
    }
    return label;
}

function sourceLabel(item) {
    const map = {text: "Текст", news: "Новость", product: "Товар"};
    return map[item.source_type] || "—";
}

async function openCreate() {
    editing.value = {};
    showForm.value = true;
    resetForm();
    // Подставляем подпись по умолчанию из страницы custom_pages (slug = default-sign)
    const signature = await adminStore.loadDefaultSignature();
    if (signature && !form.signature) form.signature = signature;
    refreshRecipientsCount();
}

function openEdit(item) {
    editing.value = item;
    showForm.value = true;
    Object.assign(form, {
        subject: item.subject || "",
        body: item.body || "",
        signature: item.signature || "",
        audience_type: item.audience_type || "all",
        audience_value: item.audience_value || "",
        audience_attr_value: item.audience_attr_value || "",
        source_type: item.source_type || "text",
        source_id: item.source_id || "",
    });
    refreshRecipientsCount();
}

function cancelForm() {
    editing.value = null;
    showForm.value = false;
    resetForm();
}

async function loadMailings() {
    await adminStore.loadMailings({page: 1, per_page: PER_PAGE});
}

let recipientsRequestId = 0;
let recipientsTimer = null;

// Счётчик обновляется с небольшой задержкой, чтобы не дёргать API на каждый символ
function scheduleRecipientsCount() {
    if (recipientsTimer) clearTimeout(recipientsTimer);
    recipientsTimer = setTimeout(refreshRecipientsCount, 300);
}

async function refreshRecipientsCount() {
    const requestId = ++recipientsRequestId;
    if ((form.audience_type === "role" || form.audience_type === "attribute") && !form.audience_value) {
        recipientsCount.value = 0;
        return;
    }
    const count = await adminStore.loadRecipientsCount({
        audience_type: form.audience_type,
        audience_value: form.audience_value,
        audience_attr_value: form.audience_attr_value,
    });
    // Игнорируем устаревший ответ, если параметры успели измениться
    if (requestId !== recipientsRequestId) return;
    recipientsCount.value = count;
}

// Подставляет текст источника (новость/товар) в тело письма
function applySourceText(text) {
    const value = (text || "").trim();
    if (!value) return;
    form.body = form.body ? form.body + "\n\n" + value : value;
}

function onNewsSelect() {
    const news = (adminStore.news || []).find(item => item.id === form.source_id);
    if (news) applySourceText(news.body);
}

function onProductSelect() {
    const product = (adminStore.products || []).find(item => item.id === form.source_id);
    if (product) applySourceText(product.description);
}

watch(() => form.source_type, (value) => {
    if (value === "text") form.source_id = "";
});

watch(() => [form.audience_type, form.audience_value, form.audience_attr_value], scheduleRecipientsCount);

async function saveHandler() {
    const payload = {
        subject: form.subject,
        body: form.body,
        signature: form.signature,
        audience_type: form.audience_type,
        audience_value: form.audience_value,
        audience_attr_value: form.audience_attr_value,
        source_type: form.source_type,
        source_id: form.source_type === "text" ? "" : form.source_id,
    };
    const isEdit = !!editing.value?.id;
    const result = isEdit
        ? await adminStore.updateMailing(editing.value.id, payload)
        : await adminStore.createMailing(payload);
    if (adminStore.error) {
        toast.error(adminStore.error);
        return;
    }
    if (result) {
        toast.success(isEdit ? "Рассылка обновлена" : "Черновик рассылки создан");
        cancelForm();
        await loadMailings();
    }
}

async function testHandler() {
    if (!testEmail.value.trim()) {
        toast.error("Укажите email для тестовой отправки");
        return;
    }
    const ok = await adminStore.testMailing(editing.value.id, testEmail.value.trim());
    if (adminStore.error) {
        toast.error(adminStore.error);
        return;
    }
    if (ok) toast.success("Тестовое письмо отправлено");
}

async function openSend(item) {
    sendTarget.value = item;
    sendMode.value = "all";
    sendRecipientsCount.value = null;
    showSend.value = true;
    await refreshSendRecipientsCount();
}

async function refreshSendRecipientsCount() {
    if (!sendTarget.value) return;
    sendRecipientsCount.value = await adminStore.loadMailingRecipientsCount(sendTarget.value.id, sendMode.value);
}

watch(sendMode, refreshSendRecipientsCount);

async function confirmSend() {
    const item = sendTarget.value;
    if (!item) return;
    const result = await adminStore.sendMailing(item.id, sendMode.value);
    if (adminStore.error) {
        toast.error(adminStore.error);
        return;
    }
    if (result) {
        toast.success("Рассылка запущена");
        showSend.value = false;
        sendTarget.value = null;
        await loadMailings();
    }
}

async function openProtocol(item) {
    protocolTarget.value = item;
    deliveriesRun.value = 0;
    deliveriesPage.value = 1;
    showProtocol.value = true;
    runs.value = await adminStore.loadMailingRuns(item.id);
    await loadDeliveries();
}

async function loadDeliveries() {
    if (!protocolTarget.value) return;
    const params = {page: deliveriesPage.value, per_page: PER_PAGE};
    if (deliveriesRun.value > 0) params.run_no = deliveriesRun.value;
    const result = await adminStore.loadMailingDeliveries(protocolTarget.value.id, params);
    deliveries.value = result.data;
    deliveriesTotal.value = result.total;
}

async function changeDeliveriesPage(delta) {
    const next = deliveriesPage.value + delta;
    if (next < 1) return;
    if (next > Math.ceil(deliveriesTotal.value / PER_PAGE)) return;
    deliveriesPage.value = next;
    await loadDeliveries();
}

async function openRuns(item) {
    runsTarget.value = item;
    showRuns.value = true;
    runs.value = await adminStore.loadMailingRuns(item.id);
}

onMounted(async () => {
    await loadMailings();
    await adminStore.loadRoles();
    await adminStore.loadNewsData();
    await adminStore.loadProductsData();
    await refreshRecipientsCount();
});

</script>

<template>
  <div class="adminCrud">
    <MainPreloader
        v-if="adminStore.loading"
        position="fixed"
        :size="80"
        :stroke="3"
    />

    <div class="adminCrud__toolbar">
      <div>
        <div class="adminCrud__toolbarTitle">Рассылки</div>
        <div class="adminCrud__toolbarHint">Всего рассылок: {{ adminStore.total ?? '—' }}</div>
      </div>
      <ActionButton style="background-color: #6FC062; color: #212226;" @buttonClick="openCreate">
        <template #caption>Создать рассылку</template>
      </ActionButton>
    </div>

    <AdminModal
        :visible="showForm"
        :title="editing?.id ? 'Редактирование рассылки' : 'Новая рассылка'"
        @close="cancelForm"
    >
      <div class="adminCrud__formGrid">
        <div class="adminCrud__field adminCrud__field--wide">
          <label class="adminCrud__label">Тема письма</label>
          <form-input v-model="form.subject"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Аудитория</label>
          <select v-model="form.audience_type" class="adminCrud__select">
            <option v-for="option in audienceTypes" :key="option.value" :value="option.value">{{ option.label }}</option>
          </select>
        </div>
        <div v-if="form.audience_type === 'role'" class="adminCrud__field">
          <label class="adminCrud__label">Роль</label>
          <select v-model="form.audience_value" class="adminCrud__select">
            <option value="">— выберите роль —</option>
            <option v-for="option in roleOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
          </select>
        </div>
        <template v-if="form.audience_type === 'attribute'">
          <div class="adminCrud__field">
            <label class="adminCrud__label">Ключ атрибута</label>
            <form-input v-model="form.audience_value"/>
          </div>
          <div class="adminCrud__field">
            <label class="adminCrud__label">Значение (необязательно)</label>
            <form-input v-model="form.audience_attr_value"/>
          </div>
        </template>
        <div class="adminCrud__field adminCrud__field--wide">
          <label class="adminCrud__label">Получателей по выбранной аудитории</label>
          <div class="adminCrud__counter">{{ recipientsCount ?? '—' }}</div>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Источник</label>
          <select v-model="form.source_type" class="adminCrud__select">
            <option v-for="option in sourceTypes" :key="option.value" :value="option.value">{{ option.label }}</option>
          </select>
        </div>
        <div v-if="form.source_type === 'news'" class="adminCrud__field">
          <label class="adminCrud__label">Новость</label>
          <select v-model="form.source_id" class="adminCrud__select" @change="onNewsSelect">
            <option value="">— выберите новость —</option>
            <option v-for="option in newsOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
          </select>
        </div>
        <div v-if="form.source_type === 'product'" class="adminCrud__field">
          <label class="adminCrud__label">Товар</label>
          <select v-model="form.source_id" class="adminCrud__select" @change="onProductSelect">
            <option value="">— выберите товар —</option>
            <option v-for="option in productOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
          </select>
        </div>
        <div class="adminCrud__field adminCrud__field--wide adminCrud__field--content">
          <label class="adminCrud__label">Сообщение (HTML)</label>
          <form-input v-model="form.body" rows="20"/>
        </div>
        <div class="adminCrud__field adminCrud__field--wide adminCrud__field--content-small">
          <label class="adminCrud__label">Подпись</label>
          <form-input v-model="form.signature" rows="4"/>
        </div>
        <div v-if="editing?.id" class="adminCrud__field adminCrud__field--wide">
          <label class="adminCrud__label">Email для тестовой отправки</label>
          <form-input v-model="testEmail"/>
        </div>
      </div>
      <template #footer>
        <ActionButton @buttonClick="cancelForm">
          <template #caption>Отмена</template>
        </ActionButton>
        <ActionButton v-if="editing?.id" @buttonClick="testHandler">
          <template #caption>Тестовая отправка</template>
        </ActionButton>
        <ActionButton style="background-color: #6FC062; color: #212226;" @buttonClick="saveHandler">
          <template #caption>Сохранить</template>
        </ActionButton>
      </template>
    </AdminModal>

    <AdminModal
        :visible="showSend"
        :title="sendTarget?.status === 'draft' ? 'Запуск рассылки' : 'Повторная отправка'"
        @close="showSend = false"
    >
      <div v-if="canChooseSendMode" class="adminCrud__field">
        <label class="adminCrud__label">Режим отправки</label>
        <select v-model="sendMode" class="adminCrud__select">
          <option value="all">Всем получателям</option>
          <option value="failed">Только неудачным</option>
        </select>
      </div>
      <div v-else class="adminCrud__field">
        <div class="adminCrud__hint">Режим: всем получателям</div>
      </div>
      <div class="adminCrud__field">
        <div class="adminCrud__hint">Получателей: {{ sendRecipientsCount ?? '—' }}</div>
      </div>
      <template #footer>
        <ActionButton @buttonClick="showSend = false">
          <template #caption>Отмена</template>
        </ActionButton>
        <ActionButton
            :disabled="!sendRecipientsCount"
            style="background-color: #6FC062; color: #212226;"
            @buttonClick="confirmSend"
        >
          <template #caption>Отправить</template>
        </ActionButton>
      </template>
    </AdminModal>

    <AdminModal
        :visible="showProtocol"
        :title="`Протокол доставки: ${protocolTarget?.subject || ''}`"
        @close="showProtocol = false"
    >
      <div class="adminCrud__field">
        <label class="adminCrud__label">Запуск</label>
        <select v-model.number="deliveriesRun" class="adminCrud__select" @change="deliveriesPage = 1; loadDeliveries()">
          <option :value="0">Все запуски</option>
          <option v-for="run in runs" :key="run.run_no" :value="run.run_no">Запуск №{{ run.run_no }}</option>
        </select>
      </div>
      <div v-if="deliveries.length" class="adminCrud__tableWrap">
        <table class="adminTable">
          <thead>
          <tr>
            <th>Email</th>
            <th>Запуск</th>
            <th>Статус</th>
            <th>Ошибка</th>
            <th>Дата</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="delivery in deliveries" :key="delivery.id">
            <td>{{ delivery.email }}</td>
            <td>{{ delivery.run_no }}</td>
            <td>{{ delivery.status === 'sent' ? 'Отправлено' : 'Ошибка' }}</td>
            <td>{{ delivery.error || '—' }}</td>
            <td>{{ formatDate(delivery.sent_at) }}</td>
          </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="adminCrud__hint">Записей нет</div>
      <div class="adminCrud__pager">
        <span class="adminCrud__hint">Всего: {{ deliveriesTotal }}</span>
        <button
            class="adminCrud__actionButton"
            :disabled="deliveriesPage <= 1"
            @click="changeDeliveriesPage(-1)"
        >Назад</button>
        <span class="adminCrud__hint">Стр. {{ deliveriesPage }}</span>
        <button
            class="adminCrud__actionButton"
            :disabled="deliveriesPage >= Math.ceil(deliveriesTotal / PER_PAGE)"
            @click="changeDeliveriesPage(1)"
        >Вперёд</button>
      </div>
    </AdminModal>

    <AdminModal
        :visible="showRuns"
        :title="`Отчёт по запускам: ${runsTarget?.subject || ''}`"
        @close="showRuns = false"
    >
      <div v-if="runs.length" class="adminCrud__tableWrap">
        <table class="adminTable">
          <thead>
          <tr>
            <th>Запуск</th>
            <th>Всего</th>
            <th>Отправлено</th>
            <th>Ошибки</th>
            <th>Начало</th>
            <th>Конец</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="run in runs" :key="run.run_no">
            <td>№{{ run.run_no }}</td>
            <td>{{ run.total }}</td>
            <td>{{ run.sent }}</td>
            <td>{{ run.failed }}</td>
            <td>{{ formatDate(run.started_at) }}</td>
            <td>{{ formatDate(run.finished_at) }}</td>
          </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="adminCrud__hint">Запусков пока нет</div>
    </AdminModal>

    <EmptyCard v-if="!items.length && !adminStore.loading"/>

    <table v-else-if="items.length" class="adminTable">
      <thead>
      <tr>
        <th>Тема</th>
        <th>Аудитория</th>
        <th>Источник</th>
        <th>Статус</th>
        <th>Получатели</th>
        <th>Отправлено</th>
        <th>Ошибки</th>
        <th>Дата</th>
        <th>Действия</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="item in items" :key="item.id">
        <td>{{ item.subject || '—' }}</td>
        <td>{{ audienceLabel(item) }}</td>
        <td>{{ sourceLabel(item) }}</td>
        <td>{{ statusLabel(item.status) }}</td>
        <td>{{ item.recipient_count ?? '—' }}</td>
        <td>{{ item.sent_count ?? '—' }}</td>
        <td>{{ item.failed_count ?? '—' }}</td>
        <td>{{ formatDate(item.created_at) }}</td>
        <td class="adminCrud__actions">
          <button
              v-if="item.status === 'draft'"
              class="adminCrud__actionButton"
              title="Изменить"
              @click="openEdit(item)"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <button
              v-if="item.status !== 'sending'"
              class="adminCrud__actionButton"
              :title="item.status === 'draft' ? 'Отправить' : 'Повторить'"
              @click="openSend(item)"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="m22 2-7 20-4-9-9-4 20-7Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M22 2 11 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <button
              v-if="(item.run_count || 0) > 0"
              class="adminCrud__actionButton"
              title="Протокол доставки"
              @click="openProtocol(item)"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M14 2v6h6M9 13h6M9 17h6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <button
              v-if="(item.run_count || 0) > 0"
              class="adminCrud__actionButton"
              title="Отчёт по запускам"
              @click="openRuns(item)"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 3v18h18M7 15l3-4 3 3 4-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </td>
      </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped lang="scss">
.adminCrud {
  display: flex;
  flex-direction: column;
  gap: 14px;

  &__toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__toolbarTitle {
    color: var(--cd-base-green);
    font-size: 16px;
    font-family: var(--cd-default-font);
  }

  &__toolbarHint {
    color: var(--cd-text-muted);
    font-size: 13px;
    margin-top: 4px;
  }

  &__formGrid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    @include respond-to('md') {
      grid-template-columns: 1fr;
    }
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;

    &--wide { grid-column: 1 / -1; }

    &--content,
    &--content-small {
      :deep(.formInput) {
        width: 100%;
        height: auto;
        resize: vertical;
      }
    }

    &--content :deep(.formInput) { min-height: 30vh; }
    &--content-small :deep(.formInput) { min-height: 90px; }
  }

  &__label {
    color: var(--cd-gray-alt);
    font-size: 13px;
  }

  &__hint {
    color: var(--cd-text-muted);
    font-size: 13px;
  }

  &__counter {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    align-self: flex-start;
    min-width: 64px;
    height: 48px;
    padding: 0 20px;
    border-radius: 20px;
    background: var(--cd-field-bg);
    border: 1px solid var(--cd-base-green);
    color: var(--cd-base-green);
    font-size: 16px;
    font-weight: 600;
  }

  &__select {
    width: 100%;
    height: 48px;
    border-radius: 20px;
    padding: 5px 20px;
    background: var(--cd-field-bg);
    color: var(--cd-field-color);
    border: 1px solid var(--cd-field-border);
    font-size: 14px;
    font-family: var(--cd-default-font);

    &:focus {
      outline: none;
      border-color: var(--cd-base-green);
      box-shadow: 0 0 5px rgba(98, 192, 100, 0.3);
    }
  }

  &__tableWrap {
    max-height: 50vh;
    overflow-y: auto;
  }

  &__pager {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 12px;
  }

  &__actions {
    vertical-align: middle;
    white-space: nowrap;
    font-size: 0;

    .adminCrud__actionButton + .adminCrud__actionButton {
      margin-left: 8px;
    }
  }

  &__actionButton {
    background: transparent;
    border: 1px solid var(--cd-base-green);
    color: var(--cd-base-green);
    border-radius: 8px;
    padding: 6px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s;

    &:hover { background: var(--cd-base-green); color: #ffffff; }
    &:disabled { opacity: 0.4; cursor: default; }
    &:disabled:hover { background: transparent; color: var(--cd-base-green); }
  }
}

.adminTable {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 8px;
  text-align: left;

  th {
    color: var(--cd-text-muted);
    font-size: 12px;
    font-weight: 400;
    padding: 0 16px 4px;
  }

  td {
    background: var(--cd-card-bg);
    padding: 12px 16px;
    font-size: 14px;
    color: var(--cd-text);

    &:first-child { border-radius: 12px 0 0 12px; }
    &:last-child { border-radius: 0 12px 12px 0; }
  }
}
</style>
